import { useMemo, useState } from 'react';
import styles from './PurposeModePanel.module.css';
import { brandConfig } from '../../../config/brands';
import { findNodeByKey } from '../utils/templateCapture';
import type { AttributeTreeNode } from '../state/useGenerationState';
import {
  usePurposeSuggestions,
  type PurposeKind,
  type BannerTemplateSuggestion,
} from '../hooks/usePurposeSuggestions';
import type { RecommendationSubject } from '../hooks/useRecommendations';
import { useRecommendationEvents } from '../hooks/useRecommendationEvents';

const PURPOSE_CHOICES: { key: PurposeKind; label: string; description: string }[] = [
  { key: 'product_shot', label: '商品画像', description: '物撮り・白背景' },
  { key: 'scene', label: '使用シーンカット', description: '生活シーンで商品を見せる' },
  { key: 'banner', label: 'バナー広告', description: 'テンプレートから選ぶ' },
  { key: 'sns', label: 'SNS投稿画像', description: '縦長・SNS向け構図' },
];

/** 直近に選んだ目的タブを覚えておく sessionStorage キー。タブ切替のたびに 'product_shot' へ
 *  戻ってしまう (毎マウント初期化) のを防ぐため、初期値の決定にのみ使う。 */
const LAST_PURPOSE_STORAGE_KEY = 'purpose_mode_last_kind';

function isPurposeKind(value: string | null): value is PurposeKind {
  return value === 'product_shot' || value === 'scene' || value === 'banner' || value === 'sns';
}

/** マウント毎の初期 purpose を決定する。purpose-only テンプレ (バナー等) 適用中は
 *  常に 'banner' を優先し、それ以外は直近選択タブ → 既定 'product_shot' の順で決める。 */
function resolveInitialPurpose(activeTemplateIsPurposeOnly: boolean): PurposeKind {
  if (activeTemplateIsPurposeOnly) return 'banner';
  try {
    const saved = sessionStorage.getItem(LAST_PURPOSE_STORAGE_KEY);
    if (isPurposeKind(saved)) return saved;
  } catch {
    // sessionStorage が使えない環境 (プライベートモード等) は既定値にフォールバック
  }
  return 'product_shot';
}

/** preset_selections の { groupKey: optionKey } をラベルへ解決する。ツリー未取得/一致なしは null。 */
function resolvePresetLabel(
  tree: AttributeTreeNode[],
  groupKey: string,
  optionKey: string,
): { groupLabel: string; optionLabel: string } | null {
  const groupNode = findNodeByKey(tree, groupKey);
  if (!groupNode) return null;
  const optionNode = findNodeByKey(groupNode.children, optionKey);
  return { groupLabel: groupNode.label, optionLabel: optionNode?.label ?? optionKey };
}

export type PurposeApplyPresetInput = {
  aspectRatio: string;
  presetSelections: Record<string, string>;
  personIncluded: boolean;
};

type PurposeModePanelProps = {
  /** 選択中素材の先頭1件から導出した subject。未選択なら null (プロファイル無し前提で degrade)。 */
  subject: RecommendationSubject | null;
  /** 現在テンプレート適用中の group key + option key (banner_ad 選択状態の判定用)。未適用は null。 */
  activeTemplate: { group: { key: string }; optionKey: string } | null;
  /** activeTemplate が purpose_only (バナー等・目的軸モード専用) の group か。true の間は
   *  マウント時の初期タブを 'banner' に固定する。 */
  activeTemplateIsPurposeOnly: boolean;
  /** テンプレート選択 (banner_ad 等) を適用する。isTemplateActive のロック機構に乗せるため
   *  GenerationWorkspace の selectTemplateOption をそのまま渡してもらう。 */
  onSelectBannerTemplate: (groupKey: string, optionKey: string) => void;
  /** テンプレート選択を解除する。 */
  onClearBannerTemplate: (groupKey: string) => void;
  /** 商品画像/シーン/SNS: 目的プリセットを属性へ適用し、属性モードへ遷移する。 */
  onApplyPreset: (input: PurposeApplyPresetInput) => void;
  /** preset_selections のラベル解決用。読み込み中は空配列でも表示は崩れない。 */
  attributeTree: AttributeTreeNode[];
  /** バナーテンプレの [MAIN_COPY] / [CTA_TEXT] へ配線する値。settings.attributes.main_copy / cta と同期。 */
  copyValues: { mainCopy: string; cta: string };
  /** メインコピー/CTA の入力変更。GenerationWorkspace 側で setAttribute へ渡す想定。 */
  onChangeCopy: (field: 'main_copy' | 'cta', value: string) => void;
};

export function PurposeModePanel({
  subject,
  activeTemplate,
  activeTemplateIsPurposeOnly,
  onSelectBannerTemplate,
  onClearBannerTemplate,
  onApplyPreset,
  attributeTree,
  copyValues,
  onChangeCopy,
}: PurposeModePanelProps) {
  const [purpose, setPurpose] = useState<PurposeKind>(() => resolveInitialPurpose(activeTemplateIsPurposeOnly));
  const query = usePurposeSuggestions(purpose, subject);
  const data = query.data;

  const handleSelectPurpose = (kind: PurposeKind) => {
    setPurpose(kind);
    try {
      sessionStorage.setItem(LAST_PURPOSE_STORAGE_KEY, kind);
    } catch {
      // sessionStorage が使えない環境は保存をスキップするだけで機能自体は継続する
    }
  };

  return (
    <section className={styles.panel}>
      <div className={styles.purposeChoices}>
        {PURPOSE_CHOICES.map((choice) => (
          <button
            key={choice.key}
            type="button"
            className={`${styles.purposeChoice} ${purpose === choice.key ? styles.purposeChoiceActive : ''}`}
            onClick={() => handleSelectPurpose(choice.key)}
          >
            <span className={styles.purposeChoiceLabel}>{choice.label}</span>
            <span className={styles.purposeChoiceDescription}>{choice.description}</span>
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <div className={styles.loading}>読み込み中…</div>
      ) : purpose === 'banner' ? (
        <BannerPurposeContent
          data={data && data.status === 'ready' && data.purpose === 'banner' ? data : undefined}
          isUnavailable={!data || data.status === 'unavailable' || query.isError}
          subject={subject}
          activeTemplate={activeTemplate}
          onSelectBannerTemplate={onSelectBannerTemplate}
          onClearBannerTemplate={onClearBannerTemplate}
          copyValues={copyValues}
          onChangeCopy={onChangeCopy}
        />
      ) : (
        <PresetPurposeContent
          data={data && data.status === 'ready' && data.purpose !== 'banner' ? data : undefined}
          isUnavailable={!data || data.status === 'unavailable' || query.isError}
          attributeTree={attributeTree}
          onApplyPreset={onApplyPreset}
          purpose={purpose}
          subject={subject}
        />
      )}
    </section>
  );
}

function BannerPurposeContent({
  data,
  isUnavailable,
  subject,
  activeTemplate,
  onSelectBannerTemplate,
  onClearBannerTemplate,
  copyValues,
  onChangeCopy,
}: {
  data: Extract<ReturnType<typeof usePurposeSuggestions>['data'], { status: 'ready'; purpose: 'banner' }> | undefined;
  isUnavailable: boolean;
  subject: RecommendationSubject | null;
  activeTemplate: { group: { key: string }; optionKey: string } | null;
  onSelectBannerTemplate: (groupKey: string, optionKey: string) => void;
  onClearBannerTemplate: (groupKey: string) => void;
  copyValues: { mainCopy: string; cta: string };
  onChangeCopy: (field: 'main_copy' | 'cta', value: string) => void;
}) {
  const { logRecommendationEvent } = useRecommendationEvents();
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const templates = data?.banner.templates ?? [];

  // 出現順で重複除去したカテゴリ一覧。絞り込みは表示のみでランキング (score/rank) 順は変えない。
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const tpl of templates) {
      if (!seen.has(tpl.category_label)) {
        seen.add(tpl.category_label);
        list.push(tpl.category_label);
      }
    }
    return list;
  }, [templates]);

  if (isUnavailable || !data) {
    return (
      <div className={styles.unavailableNotice}>
        バナーテンプレートは準備中です。属性選択モードから背景・構図を指定して生成してください。
      </div>
    );
  }

  const groupKey = data.banner.group_key;
  const isSelected = (optionKey: string) => activeTemplate?.group.key === groupKey && activeTemplate.optionKey === optionKey;
  const anySelected = activeTemplate?.group.key === groupKey;
  const activeTemplateLabel = anySelected
    ? templates.find((tpl) => tpl.option_key === activeTemplate?.optionKey)?.label
    : undefined;

  // rank (表示順位) は元の (未絞り込み) 一覧での位置を使う。カテゴリ絞り込みで
  // 表示順が変わってもログの意味が揺れないようにするため。
  const templatesWithRank = templates.map((tpl, index) => ({ tpl, rank: index + 1 }));
  const visibleTemplates = categoryFilter
    ? templatesWithRank.filter(({ tpl }) => tpl.category_label === categoryFilter)
    : templatesWithRank;

  // バナーカードの採用/解除ログ (計画書§8 P4)。rank (表示順位) と category_match は
  // 一覧表示に使っている data.banner.templates の並び・フラグをそのまま添える。
  const logBannerEvent = (action: 'adopted' | 'cleared', tpl: BannerTemplateSuggestion, rank: number) => {
    logRecommendationEvent({
      subject_type: subject?.subject_type ?? null,
      subject_id: subject?.subject_id ?? null,
      surface: 'purpose_banner',
      action,
      group_key: groupKey,
      option_key: tpl.option_key,
      score: tpl.score,
      profile_status: data.profile_status,
      metadata: { rank, category_match: tpl.category_match },
    });
  };

  return (
    <div>
      {!subject && (
        <p className={styles.hintText}>
          {brandConfig.labels.productShort}素材を選択すると、内容に合わせておすすめ順に並び替わります。
        </p>
      )}
      {anySelected && (
        <>
          <div className={styles.selectedBar}>
            <span>
              {activeTemplateLabel ? `テンプレート「${activeTemplateLabel}」を適用中です。` : 'テンプレートを適用中です。'}
            </span>
            <button
              type="button"
              className={styles.clearLink}
              onClick={() => {
                onClearBannerTemplate(groupKey);
                const activeIndex = templates.findIndex((tpl) => tpl.option_key === activeTemplate?.optionKey);
                if (activeIndex >= 0) logBannerEvent('cleared', templates[activeIndex], activeIndex + 1);
              }}
            >
              選択を解除
            </button>
          </div>
          <div className={styles.copyFieldsBlock}>
            <div className={styles.copyFieldsRow}>
              <label className={styles.copyField}>
                <span>メインコピー</span>
                <input
                  type="text"
                  value={copyValues.mainCopy}
                  onChange={(event) => onChangeCopy('main_copy', event.target.value)}
                  placeholder="例: 今だけ初回半額"
                />
              </label>
              <label className={styles.copyField}>
                <span>CTA</span>
                <input
                  type="text"
                  value={copyValues.cta}
                  onChange={(event) => onChangeCopy('cta', event.target.value)}
                  placeholder="例: 今すぐ購入"
                />
              </label>
            </div>
            <p className={styles.copyFieldsHint}>未入力の場合は AI が商品に合わせて文言を作成します</p>
          </div>
        </>
      )}
      {templates.length === 0 ? (
        <div className={styles.unavailableNotice}>利用可能なバナーテンプレートがありません。</div>
      ) : (
        <>
          {categories.length > 0 && (
            <div className={styles.categoryFilterRow}>
              <button
                type="button"
                className={`${styles.categoryChip} ${categoryFilter === null ? styles.categoryChipActive : ''}`}
                onClick={() => setCategoryFilter(null)}
              >
                すべて
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`${styles.categoryChip} ${categoryFilter === category ? styles.categoryChipActive : ''}`}
                  onClick={() => setCategoryFilter(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
          <div className={styles.bannerGrid}>
            {visibleTemplates.map(({ tpl, rank }) => (
              <BannerTemplateCard
                key={tpl.option_key}
                template={tpl}
                selected={isSelected(tpl.option_key)}
                onClick={() => {
                  if (isSelected(tpl.option_key)) {
                    onClearBannerTemplate(groupKey);
                    logBannerEvent('cleared', tpl, rank);
                  } else {
                    onSelectBannerTemplate(groupKey, tpl.option_key);
                    logBannerEvent('adopted', tpl, rank);
                  }
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BannerTemplateCard({
  template,
  selected,
  onClick,
}: {
  template: BannerTemplateSuggestion;
  selected: boolean;
  onClick: () => void;
}) {
  const [imageError, setImageError] = useState(false);

  return (
    <button
      type="button"
      className={`${styles.bannerCard} ${selected ? styles.bannerCardSelected : ''}`}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={template.label}
    >
      {template.image_url && !imageError ? (
        <img
          src={template.image_url}
          alt={template.label}
          className={styles.bannerCardImage}
          onError={() => setImageError(true)}
        />
      ) : (
        <div className={styles.bannerCardImagePlaceholder} />
      )}
      <div className={styles.bannerCardMeta}>
        <span className={styles.bannerCardLabel} title={template.label}>{template.label}</span>
        <span className={styles.bannerCardCategory}>{template.category_label}</span>
      </div>
      {template.category_match && <span className={styles.recommendedBadge}>おすすめ</span>}
      {selected && <span className={styles.selectedBadge}>選択中</span>}
    </button>
  );
}

function PresetPurposeContent({
  data,
  isUnavailable,
  attributeTree,
  onApplyPreset,
  purpose,
  subject,
}: {
  data: Extract<ReturnType<typeof usePurposeSuggestions>['data'], { status: 'ready'; purpose: 'product_shot' | 'scene' | 'sns' }> | undefined;
  isUnavailable: boolean;
  attributeTree: AttributeTreeNode[];
  onApplyPreset: (input: PurposeApplyPresetInput) => void;
  purpose: PurposeKind;
  subject: RecommendationSubject | null;
}) {
  const { logRecommendationEvent } = useRecommendationEvents();

  if (isUnavailable || !data) {
    return (
      <div className={styles.unavailableNotice}>
        現在この目的の提案はご利用いただけません。属性選択モードから直接お選びください。
      </div>
    );
  }

  const chips = Object.entries(data.preset_selections)
    .map(([groupKey, optionKey]) => resolvePresetLabel(attributeTree, groupKey, optionKey))
    .filter((v): v is { groupLabel: string; optionLabel: string } => v !== null);

  return (
    <div className={styles.presetContent}>
      <div className={styles.presetSummaryRow}>
        <span className={styles.presetSummaryItem}>アスペクト比: {data.aspect_ratio}</span>
        <span className={styles.presetSummaryItem}>人物: {data.person_included ? 'あり' : 'なし'}</span>
      </div>
      {chips.length > 0 ? (
        <div className={styles.presetChips}>
          {chips.map((chip, i) => (
            <span key={i} className={styles.presetChip}>
              {chip.groupLabel}: {chip.optionLabel}
            </span>
          ))}
        </div>
      ) : (
        <p className={styles.hintText}>
          この目的では、アスペクト比と人物の有無のみが適用されます。背景・シーンなどの詳細は、適用後に属性画面で調整してください。
        </p>
      )}
      <button
        type="button"
        className={styles.applyButton}
        onClick={() => {
          onApplyPreset({
            aspectRatio: data.aspect_ratio,
            presetSelections: data.preset_selections,
            personIncluded: data.person_included,
          });
          // 目的軸プリセットの採用ログ (計画書§8 P4)。個別 group/option 単位ではなく
          // プリセット一括適用のため、purpose と適用したキー数のみ metadata に残す。
          logRecommendationEvent({
            subject_type: subject?.subject_type ?? null,
            subject_id: subject?.subject_id ?? null,
            surface: 'purpose_preset',
            action: 'adopted',
            metadata: { purpose, preset_key_count: Object.keys(data.preset_selections).length },
          });
        }}
      >
        この内容で属性画面へ
      </button>
    </div>
  );
}
